import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import * as Icon from 'react-feather';
import MainStructure from '../components/general/MainStructure';
import { useAuth } from '../AuthContext';
import TimezoneSelector from './components/TimezoneSelector';
import linkedinLogo from '../assets/linkedin.png';
import 'react-datepicker/dist/react-datepicker.css';

// LinkedIn has no native carousel: a carousel is a document post, a PDF whose
// pages LinkedIn shows as swipeable slides. The slides chosen here are turned
// into that PDF by the scheduler at publish time.

const MIN_SLIDES = 2;
const MAX_SLIDES = 20;
const TITLE_MAX = 100;
const CAPTION_MAX = 3000;
const SLIDE_TEXT_MAX = 200;

const UPLOAD_URL = 'https://api.schedpilot.com/wp-json/custom/v1/upload-image';
const CREATE_URL = 'https://api.schedpilot.com/wp-json/api/v1/create-linkedin-carousel';

const formatLocalDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const card = { background: '#fff', border: '1px solid #e8edf2', borderRadius: 14, padding: 20, marginBottom: 16 };
const label = { fontWeight: 700, fontSize: '0.95rem', marginBottom: 4, color: '#0f172a' };
const hint = { fontSize: '0.78rem', color: '#64748b', marginBottom: 12 };
const counter = (n, max) => ({ fontSize: '0.72rem', color: n > max ? '#dc2626' : '#94a3b8', textAlign: 'right', marginTop: 4 });

function Avatar({ src, name, size = 34 }) {
  const [failed, setFailed] = useState(false);
  const letter = (name || '?')[0].toUpperCase();
  if (failed || !src) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', background: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.4, flexShrink: 0 }}>
        {letter}
      </div>
    );
  }
  return <img src={src} alt={name} onError={() => setFailed(true)} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
}

/* A rough LinkedIn post card, so people can see the order and the title
   before scheduling. It is a guide, not a pixel-accurate render. */
function Preview({ slides, title, caption, account }) {
  const ready = slides.filter((s) => s.status === 'done' || s.status === 'uploading');
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (index > ready.length - 1) setIndex(Math.max(0, ready.length - 1));
  }, [ready.length, index]);

  const current = ready[index];
  const long = caption.length > 180;

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px 8px' }}>
        <Avatar src={account?.image} name={account?.name || 'You'} size={40} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{account?.name || 'Your LinkedIn'}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Now · <Icon.Globe size={10} /></div>
        </div>
      </div>

      <div style={{ padding: '0 16px 10px', fontSize: '0.85rem', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {caption ? (expanded || !long ? caption : caption.slice(0, 180) + '… ') : <span style={{ color: '#cbd5e1' }}>Your caption appears here</span>}
        {long && !expanded && (
          <button type="button" onClick={() => setExpanded(true)} style={{ border: 'none', background: 'none', color: '#64748b', padding: 0, fontSize: '0.85rem', cursor: 'pointer' }}>see more</button>
        )}
      </div>

      <div style={{ background: '#f1f5f9' }}>
        <div style={{ background: '#1e293b', color: '#fff', fontSize: '0.78rem', fontWeight: 600, padding: '7px 12px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title || 'Carousel title'}</span>
          {ready.length > 0 && <span style={{ opacity: 0.75, flexShrink: 0 }}>{index + 1} / {ready.length}</span>}
        </div>

        <div style={{ position: 'relative', aspectRatio: '4 / 5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {current ? (
            <img src={current.preview || current.url} alt={`Slide ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>
              <Icon.Layers size={28} style={{ marginBottom: 8 }} /><br />Your slides appear here
            </div>
          )}

          {current?.text && (
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', padding: '10px 12px' }}>
              {current.text}
            </div>
          )}

          {index > 0 && (
            <button type="button" aria-label="Previous slide" onClick={() => setIndex(index - 1)} style={navBtn('left')}><Icon.ChevronLeft size={18} /></button>
          )}
          {index < ready.length - 1 && (
            <button type="button" aria-label="Next slide" onClick={() => setIndex(index + 1)} style={navBtn('right')}><Icon.ChevronRight size={18} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

const navBtn = (side) => ({
  position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
  width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.92)',
  boxShadow: '0 1px 4px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
});

function LinkedInCarouselPost() {
  const myContext = useAuth();
  const navigate = useNavigate();

  const linkedinAccounts = (myContext.accounts || []).filter((a) => a.type === 'linkedin' || a.type === 'linkedin_page');

  const [slides, setSlides] = useState([]);   // { key, status, id, url, preview, text }
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [submitting, setSubmitting] = useState(false);

  const nextKey = useRef(0);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  // Object URLs back the instant previews; release them when the page goes.
  useEffect(() => () => slidesRef.current.forEach((s) => s.preview && URL.revokeObjectURL(s.preview)), []);

  const patchSlide = (key, patch) => setSlides((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const uploadInto = useCallback(async (key, file) => {
    const form = new FormData();
    form.append('image', file);
    try {
      const res = await fetch(UPLOAD_URL, { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      if (data.code === 200 && data.image_id) {
        patchSlide(key, { status: 'done', id: data.image_id, url: data.file_url });
      } else {
        patchSlide(key, { status: 'error' });
      }
    } catch (e) {
      patchSlide(key, { status: 'error' });
    }
  }, []);

  // Uploads run in parallel, but each file gets its slot before it starts, so
  // the carousel keeps the order the files were chosen in rather than the
  // order the uploads happen to finish.
  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length) toast.error('Only JPG, PNG and WebP images can be used as slides.');

    const room = MAX_SLIDES - slidesRef.current.length;
    if (room <= 0) return toast.error(`A carousel can have at most ${MAX_SLIDES} slides.`);
    if (accepted.length > room) toast.warn(`Only the first ${room} image${room === 1 ? '' : 's'} were added — the limit is ${MAX_SLIDES} slides.`);

    const files = accepted.slice(0, room);
    const fresh = files.map((file) => ({
      key: ++nextKey.current, status: 'uploading', id: null, url: null,
      preview: URL.createObjectURL(file), text: '', file,
    }));
    setSlides((prev) => [...prev, ...fresh]);
    fresh.forEach((s) => uploadInto(s.key, s.file));
  }, [uploadInto]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    disabled: slides.length >= MAX_SLIDES,
  });

  const removeSlide = (key) => setSlides((prev) => {
    const gone = prev.find((s) => s.key === key);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return prev.filter((s) => s.key !== key);
  });

  const moveSlide = (index, delta) => setSlides((prev) => {
    const to = index + delta;
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    [next[index], next[to]] = [next[to], next[index]];
    return next;
  });

  const retrySlide = (slide) => {
    patchSlide(slide.key, { status: 'uploading' });
    uploadInto(slide.key, slide.file);
  };

  const toggleAccount = (value) => setSelectedAccounts((prev) => (
    prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
  ));

  // What still stands between the user and scheduling, in plain words.
  const uploading = slides.some((s) => s.status === 'uploading');
  const failedUploads = slides.filter((s) => s.status === 'error').length;
  const problems = [];
  if (slides.length < MIN_SLIDES) problems.push(`Add at least ${MIN_SLIDES} slides`);
  if (uploading) problems.push('Wait for uploads to finish');
  if (failedUploads) problems.push(`Retry or remove ${failedUploads} failed upload${failedUploads === 1 ? '' : 's'}`);
  if (!title.trim()) problems.push('Add a title');
  if (title.length > TITLE_MAX) problems.push(`Shorten the title to ${TITLE_MAX} characters`);
  if (caption.length > CAPTION_MAX) problems.push(`Shorten the caption to ${CAPTION_MAX} characters`);
  if (selectedAccounts.length === 0) problems.push('Choose a LinkedIn profile or page');

  const submit = async (postStatus) => {
    if (problems.length) return toast.error(problems[0]);

    const m = Array.isArray(myContext.membership) ? myContext.membership[0] : myContext.membership;
    const isLifetime = m?.plan?.type === 'lifetime' || (
      m?.end_date_time
        ? new Date(m.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now() + 10 * 365 * 24 * 3600 * 1000
        : false
    );
    if (!myContext.isValidMembership() && !isLifetime) {
      return toast.error('Your membership is expired. It cannot post.');
    }

    setSubmitting(true);
    try {
      const response = await fetch(CREATE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          text: caption,
          slides: slides.map((s) => ({ image_id: s.id, text: s.text.trim() })),
          accounts: selectedAccounts,
          scheduledDate: formatLocalDate(selectedDate),
          timezone,
          postStatus,
        }),
      });
      const res = await response.json();

      if (res.code !== 200) {
        toast.error(res.message || 'Could not schedule the carousel.');
        return;
      }

      if (postStatus === 'draft') {
        toast.success('Carousel saved as a draft.');
        if (myContext.addDraftItem) myContext.addDraftItem({ pid: res.data?.id, content: caption, scheduled_date: selectedDate });
        navigate('/drafts');
      } else {
        toast.success('Carousel scheduled for LinkedIn.');
        if (myContext.addScheduledItem) myContext.addScheduledItem(res.data);
        navigate('/schedule-posts');
      }
    } catch (e) {
      toast.error('Error connecting to the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const previewAccount = linkedinAccounts.find((a) => selectedAccounts.includes(`${a.id}_${a.type}`)) || linkedinAccounts[0];

  return (
    <MainStructure activeItem={'write-post'}>
      <div className="container p-0">
        {myContext.returnMembershipNotification && myContext.returnMembershipNotification()}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button type="button" onClick={() => navigate('/write-post')} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 500 }}>
            <Icon.ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 style={{ fontWeight: 800, fontSize: '1.4rem', margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={linkedinLogo} alt="" style={{ width: 22, height: 22 }} /> LinkedIn Carousel
            </h1>
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Swipeable slides, posted to LinkedIn as a document.</div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-7">

            {/* Slides */}
            <div style={card}>
              <div style={label}>Slides <span style={{ fontWeight: 500, color: '#94a3b8' }}>({slides.length}/{MAX_SLIDES})</span></div>
              <div style={hint}>
                {MIN_SLIDES}–{MAX_SLIDES} images, in the order they should appear. Portrait 4:5 (1080×1350) suits LinkedIn best;
                every slide is shown at the first slide's shape, so keep them the same size.
              </div>

              {slides.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 12, marginBottom: 12 }}>
                  {slides.map((s, i) => (
                    <div key={s.key} style={{ border: '1px solid ' + (s.status === 'error' ? '#fecaca' : '#e2e8f0'), borderRadius: 10, overflow: 'hidden', background: '#f8fafc' }}>
                      <div style={{ position: 'relative', aspectRatio: '4 / 5', background: '#fff' }}>
                        <img src={s.preview || s.url} alt={`Slide ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: s.status === 'done' ? 1 : 0.45 }} />
                        <span style={{ position: 'absolute', top: 6, left: 6, background: '#0f172a', color: '#fff', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px' }}>{i + 1}</span>
                        <button type="button" aria-label={`Remove slide ${i + 1}`} onClick={() => removeSlide(s.key)} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.75)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <Icon.X size={13} />
                        </button>
                        {s.status === 'uploading' && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>Uploading…</div>
                        )}
                        {s.status === 'error' && (
                          <button type="button" onClick={() => retrySlide(s)} style={{ position: 'absolute', inset: 0, border: 'none', background: 'rgba(254,242,242,0.85)', color: '#b91c1c', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
                            <Icon.RotateCw size={14} /><br />Upload failed — retry
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 4px 0' }}>
                        <button type="button" aria-label={`Move slide ${i + 1} earlier`} disabled={i === 0} onClick={() => moveSlide(i, -1)} style={arrowBtn(i === 0)}><Icon.ArrowLeft size={14} /></button>
                        <button type="button" aria-label={`Move slide ${i + 1} later`} disabled={i === slides.length - 1} onClick={() => moveSlide(i, 1)} style={arrowBtn(i === slides.length - 1)}><Icon.ArrowRight size={14} /></button>
                      </div>
                      <input
                        type="text"
                        value={s.text}
                        maxLength={SLIDE_TEXT_MAX}
                        placeholder="Text on slide (optional)"
                        onChange={(e) => patchSlide(s.key, { text: e.target.value })}
                        style={{ width: '100%', border: 'none', borderTop: '1px solid #e2e8f0', background: '#fff', fontSize: '0.74rem', padding: '6px 8px', marginTop: 4, outline: 'none' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {slides.length < MAX_SLIDES && (
                <div {...getRootProps()} style={{ border: `2px dashed ${isDragActive ? '#0a66c2' : '#cbd5e1'}`, background: isDragActive ? '#eff6ff' : '#f8fafc', borderRadius: 12, padding: slides.length ? 16 : 32, textAlign: 'center', cursor: 'pointer' }}>
                  <input {...getInputProps()} />
                  <Icon.UploadCloud size={slides.length ? 20 : 28} color="#0a66c2" />
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', marginTop: 6 }}>{isDragActive ? 'Drop the images here' : slides.length ? 'Add more slides' : 'Drag images here, or click to choose'}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>JPG, PNG or WebP</div>
                </div>
              )}

              {slides.some((s) => s.text.trim()) && (
                <div style={{ ...hint, marginTop: 10, marginBottom: 0, display: 'flex', gap: 6 }}>
                  <Icon.Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  Slides with text get a caption bar and are cropped to 4:5 portrait. Slides without text are kept whole.
                </div>
              )}
            </div>

            {/* Title + caption */}
            <div style={card}>
              <div style={label}>Carousel title</div>
              <div style={hint}>LinkedIn shows this on the document, above the slides.</div>
              <input type="text" className="form-control" value={title} maxLength={TITLE_MAX + 20} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 5 lessons from shipping our Q3 roadmap" />
              <div style={counter(title.length, TITLE_MAX)}>{title.length}/{TITLE_MAX}</div>

              <div style={{ ...label, marginTop: 14 }}>Caption</div>
              <textarea className="form-control" rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="What should people read before they swipe?" />
              <div style={counter(caption.length, CAPTION_MAX)}>{caption.length}/{CAPTION_MAX}</div>
            </div>

            {/* Accounts */}
            <div style={card}>
              <div style={label}>Post to</div>
              {linkedinAccounts.length === 0 ? (
                <div style={{ ...hint, marginBottom: 0 }}>
                  No LinkedIn account connected yet.{' '}
                  <button type="button" onClick={() => navigate('/social-accounts')} style={{ border: 'none', background: 'none', padding: 0, color: '#0a66c2', fontWeight: 600, cursor: 'pointer' }}>Connect LinkedIn</button>
                </div>
              ) : (
                linkedinAccounts.map((a) => {
                  const value = `${a.id}_${a.type}`;
                  const on = selectedAccounts.includes(value);
                  return (
                    <label key={value} htmlFor={`li-acc-${value}`} style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8, padding: '8px 10px', marginBottom: 4, cursor: 'pointer', background: on ? '#eff6ff' : 'transparent', border: on ? '1px solid #c7d7fd' : '1px solid transparent' }}>
                      <Avatar src={a.image} name={a.name} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{a.type === 'linkedin_page' ? 'Company page' : 'Personal profile'}</div>
                      </div>
                      <input id={`li-acc-${value}`} type="checkbox" checked={on} onChange={() => toggleAccount(value)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    </label>
                  );
                })
              )}
            </div>

            {/* Schedule */}
            <div style={card}>
              <div style={label}>When</div>
              <DatePicker selected={selectedDate} onChange={(d) => setSelectedDate(d)} showTimeSelect dateFormat="MMMM d, yyyy h:mm aa" className="form-control mb-3" />
              <TimezoneSelector value={timezone} onChange={setTimezone} label="Scheduling timezone" />

              {problems.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: '0.8rem', color: '#475569' }}>
                  {problems.map((p) => <div key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Icon.Circle size={8} /> {p}</div>)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button type="button" disabled={submitting || problems.length > 0} onClick={() => submit('publish')}
                  style={{ flex: 1, background: '#0a66c2', border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 600, fontSize: '0.9rem', color: '#fff', cursor: submitting || problems.length ? 'not-allowed' : 'pointer', opacity: submitting || problems.length ? 0.55 : 1 }}>
                  {submitting ? 'Scheduling…' : 'Schedule carousel'}
                </button>
                <button type="button" disabled={submitting || problems.length > 0} onClick={() => submit('draft')}
                  style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 9, padding: '11px 16px', fontWeight: 600, fontSize: '0.9rem', color: '#334155', cursor: submitting || problems.length ? 'not-allowed' : 'pointer', opacity: submitting || problems.length ? 0.55 : 1 }}>
                  Save draft
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div style={{ position: 'sticky', top: 16 }}>
              <div style={{ ...label, marginBottom: 8 }}>Preview</div>
              <Preview slides={slides} title={title} caption={caption} account={previewAccount} />
            </div>
          </div>
        </div>
      </div>
    </MainStructure>
  );
}

const arrowBtn = (disabled) => ({
  border: 'none', background: 'none', padding: 4, borderRadius: 6,
  color: disabled ? '#cbd5e1' : '#475569', cursor: disabled ? 'default' : 'pointer',
});

export default LinkedInCarouselPost;
